import {
  useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { bootScreenReady } from './boot';
import { supabase } from './supabase';
import { registerForPush, unregisterPush } from './push';
import { findPolicyGaps, type Policy, type PolicyGap } from './policy';
import {
  DEFAULT_DATE_FORMAT, isDateFormat, setActiveDateFormat, type DateFormatId,
} from './dateFormats';
import {
  DEFAULT_TIME_FORMAT, isTimeFormat, setActiveTimeFormat, type TimeFormatId,
} from './timeFormat';
import {
  useTheme, isColorScheme, isThemePreset, DEFAULT_PRESET,
  type ThemePreset, type ColorScheme,
} from './theme';
import { useI18n, isLanguage, type Language } from './i18n';
import { createAuthGate } from './authState';
import { todayIn, setActiveTimeZone } from './dates';
import { useQueryCache, identityOf, clearQueryCache } from './queryCache';
import { forgetAllFiles } from './files';
import { reportTechnicalFailure } from './errorReport';
import { technicalDetail } from './errorMessage';
import { SessionContext } from './sessionContext';

export interface Company {
  id: string;
  name: string;
  currency: string;
  timezone: string;
  themePreset: ThemePreset;
  locale: string;
  setupCompleted: boolean;
  /* What goes at the top of a document, as opposed to what identifies the
     company inside the app. A payslip a man takes to a bank is headed with the
     registered name, not the one somebody typed to tell two sites apart. */
  legalName: string | null;
  /** The registered name in Arabic, as transcribed from the trade licence. */
  legalNameAr: string | null;
  nameAr: string | null;
  addressLine: string | null;
  addressLineAr: string | null;
  /** Whether documents carry both languages — see migration 0096. */
  bilingualDocuments: boolean;
  /** How this company writes a date, everywhere. See migration 0097. */
  dateFormat: DateFormatId;
  timeFormat: TimeFormatId;
  tradeLicence: string | null;
  /* How somebody reaches the company about a document it issued, and who to
     ask for about a settlement. Carried on the session for the same reason
     addressLine is: it is letterhead, read when a document is built and never
     on a screen. See migration 0175. */
  phone: string | null;
  email: string | null;
  website: string | null;
  settlementContactName: string | null;
  settlementContactPhone: string | null;
}

/* =============================================================================
 * A person above the companies
 * -----------------------------------------------------------------------------
 * A platform operator belongs to no company — they have no user_profile, they
 * appear on nobody's Users screen, and deleting a company cannot delete them.
 * See migration 0105 for why that separation is structural rather than tidy.
 *
 * `actingCompanyId` is the whole of their state. Null means they are in the
 * console looking at the platform; set means they are inside that company, and
 * from that moment every policy in the database answers as though they were its
 * super admin — which is also why `profile` below is filled in for them. The
 * app does not need to know it is talking to an operator to work correctly; it
 * needs to know so it can say so on the screen.
 * ========================================================================== */
export interface PlatformOperator {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  email: string;
  /** Platform-level grants. Not the same thing as permissions inside a company. */
  permissions: string[];
  /** The company they are working inside, or null for the console. */
  actingCompanyId: string | null;
  /** What they said they were going in to do. */
  actingReason: string | null;
  actingSince: string | null;
}

export type AccountState = 'ok' | 'platform' | 'suspended' | 'disabled' | 'none';

/**
 * How long an operator stays inside a company without saying so again.
 *
 * Must match app.acting_window() in migration 0110, which is what actually
 * enforces it. This copy exists so the interface can return somebody to the
 * console rather than showing them a workspace the database has already
 * stopped answering for.
 */
export const ACTING_WINDOW_MS = 12 * 60 * 60 * 1000;

export interface Profile {
  id: string;
  companyId: string;
  employeeId: string | null;
  fullName: string;
  /**
   * The signed-in person's name in Arabic, where their employee record has one.
   *
   * `user_profiles` has no Arabic name of its own and should not grow one: the
   * name belongs to the person, the person is an employee, and a second copy on
   * the login would be a second thing to keep in step. Null for an account with
   * no employee behind it — an owner, an accountant who never clocks in — and
   * the Latin name stands, which is what it does everywhere else.
   */
  fullNameAr: string | null;
  email: string | null;
  /** What they type to sign in, when they have one. See migration 0118. */
  username: string | null;
  /**
   * ImageKit path of their picture, or null — which is the ordinary case.
   *
   * Carried on the session because the sidebar draws it on every screen, and
   * re-reading the row for one string on every navigation is a query nobody
   * needs. Replacing the picture updates this in place; see AppearanceSettings.
   */
  avatarPath: string | null;
  roleCode: string;
  roleName: string;
  /** The role's name in Arabic, from migration 0102. Null until it is set. */
  roleNameAr: string | null;
  permissions: string[];
  colorScheme: 'light' | 'dark' | 'system';
  /**
   * The language this person reads the app in.
   *
   * Theirs, not the company's. Two people signed in to the same company at the
   * same moment legitimately want different answers, and a company-wide setting
   * would make one of them permanently wrong.
   */
  locale: Language;
  /**
   * Whether the company permits this person's phone to mark them automatically.
   *
   * One of three yeses — see migration 0092. False for anybody who is not an
   * employee, which is the right answer rather than a missing one.
   */
  autoAttendance: boolean;
}

export interface SessionContextValue {
  session: Session | null;
  profile: Profile | null;
  company: Company | null;
  /** Null until an admin has completed and activated the rules. */
  policy: Policy | null;
  policyGaps: PolicyGap[];
  loading: boolean;
  /**
   * Set when there is a valid session but no usable profile behind it. Almost
   * always means the account exists in Supabase Auth but was never linked to a
   * company by bootstrap.sql — a state worth naming rather than rendering an
   * empty shell over.
   */
  profileError: string | null;
  /**
   * Why there is no workspace, when there is no workspace.
   *
   *   ok         there is one; this is what everybody sees
   *   platform   this account runs the platform and is in no company
   *   suspended  the company has been switched off
   *   disabled   this account has been switched off
   *   none       no company at all — somebody who has come to apply
   *
   * Every one of those looks identical from a client: the profile query returns
   * nothing. They need four different screens, and telling them apart is not
   * something the tables can do — they are what is being refused. So the
   * database is asked directly. See account_state in migration 0107.
   */
  accountState: AccountState;
  /**
   * Set when the signed-in account runs the platform rather than belonging to a
   * company. Null for everybody else, which is very nearly everybody.
   *
   * When this is set and `actingCompanyId` is null there is deliberately no
   * profile and no company: an operator who has not opened a company can read
   * no operational row in the system, because app.current_company_id() returns
   * null for them and every policy compares against it.
   */
  platform: PlatformOperator | null;
  /** True when the operator holds the platform grant, or holds `*`. */
  canPlatform: (permission: string) => boolean;
  /**
   * Opens a company, as that company's super admin, with a reason on record.
   *
   * Reloads the whole workspace afterwards — company, policy, permissions —
   * because every one of those answers is about to be a different company's.
   * The caller is responsible for emptying any cache of its own first; see
   * where this is called from.
   */
  enterCompany: (companyId: string, reason: string) => Promise<void>;
  /** Returns to the console and closes the visit. */
  leaveCompany: () => Promise<void>;
  /**
   * The database is behind this build and is missing a column it reads.
   *
   * Not an error — the session recovered and the app is running. It is a fact
   * the interface should be able to state, because the alternative is somebody
   * staring at an empty payroll screen with nothing anywhere telling them why.
   */
  columnsMissing: boolean;
  /** True when the caller holds the grant, or holds `*`. */
  can: (permission: string) => boolean;
  /**
   * Light/dark, applied immediately and saved to the account.
   *
   * It has to be both. Applying it locally alone means the profile's stored
   * value overwrites the choice on the next load; saving it alone means the
   * screen does not change until a refresh.
   */
  setColorScheme: (scheme: ColorScheme) => void;
  /**
   * Changes the language and remembers it against this person.
   *
   * Preferred over calling setLanguage from i18n directly: that changes the
   * screen and the device, and this also changes the record — which is what
   * makes the choice survive a new handset.
   */
  setLocale: (locale: Language) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

/* The context object lives in ./sessionContext, so its identity survives this
   module being re-evaluated by a hot reload. See src/core/i18nContext.ts. */


/**
 * Tells the server which device just signed in.
 *
 * Only ever annotates a row the trigger has already written, and only one
 * belonging to the caller from the last few minutes — so the worst a tampered
 * client achieves is misreporting its own platform. Silent on failure by
 * design: this is diagnostics, and it must not be able to break a sign-in.
 */
async function recordLoginContext(): Promise<void> {
  try {
    await supabase.rpc('record_login_context', {
      p_platform: Capacitor.getPlatform(),
      p_app_version: __APP_VERSION__,
      p_user_agent: navigator.userAgent.slice(0, 400),
    });
  } catch {
    /* diagnostics only */
  }
}

/* -----------------------------------------------------------------------------
 * What the session reads
 * -----------------------------------------------------------------------------
 * Two lists, deliberately next to each other. The first is what this build
 * wants; the second is what every database has had for a long time. The
 * difference between them is exactly the set of columns a pending migration
 * would be missing, which is what makes the fallback below safe to rely on and
 * obvious to update: add a column above, and add it to neither list below.
 * -------------------------------------------------------------------------- */

const PROFILE_COLUMNS = `
  id, company_id, employee_id, full_name, email, username, avatar_path,
  color_scheme, locale,
  roles:role_id ( code, name, name_ar, permissions ),
  employees:employee_id ( auto_attendance, full_name_ar ),
  companies:company_id (
    id, name, currency, timezone, theme_preset, locale, setup_completed,
    legal_name, legal_name_ar, name_ar, address_line, address_line_ar,
    bilingual_documents, date_format, time_format, trade_licence,
    phone, email, website, settlement_contact_name, settlement_contact_phone
  )
`;

const PROFILE_COLUMNS_BASE = `
  id, company_id, employee_id, full_name, email, username, avatar_path,
  color_scheme, locale,
  roles:role_id ( code, name, permissions ),
  employees:employee_id ( auto_attendance, full_name_ar ),
  companies:company_id (
    id, name, currency, timezone, theme_preset, locale, setup_completed,
    legal_name, trade_licence
  )
`;

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [policyGaps, setPolicyGaps] = useState<PolicyGap[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<PlatformOperator | null>(null);
  const [accountState, setAccountState] = useState<AccountState>('ok');
  const [columnsMissing, setColumnsMissing] = useState(false);

  /* -------------------------------------------------------------------------
   * The part of the cache that is written to the device
   * -------------------------------------------------------------------------
   * Scoped to the person and the company they are currently in, so an operator
   * who steps from one company into another does not carry the first one's
   * roster across. `platform?.actingCompanyId` is checked first for exactly
   * that reason: for an operator, the company they are acting as is the tenancy
   * they are inside, and `profile.companyId` is the one their own account
   * happens to belong to.
   *
   * Null while signing in, or for an operator who is not inside any company,
   * and null means nothing is read and nothing is written.
   * ---------------------------------------------------------------------- */
  useQueryCache(identityOf(
    profile?.id ?? platform?.id,
    platform?.actingCompanyId ?? profile?.companyId,
  ));
  const { setPreset, setScheme } = useTheme();
  const { setLanguage } = useI18n();

  /**
   * Guards against an out-of-order finish. Sign out while a profile load is in
   * flight and the slower request would otherwise land afterwards, restoring
   * the workspace of an account that is no longer signed in.
   */
  const loadToken = useRef(0);

  async function loadContext(activeSession: Session | null) {
    const token = ++loadToken.current;
    const isCurrent = () => loadToken.current === token;

    if (!activeSession) {
      setProfile(null);
      setCompany(null);
      setPolicy(null);
      setPolicyGaps([]);
      setProfileError(null);
      setPlatform(null);
      setAccountState('ok');
      setLoading(false);
      return;
    }

    // A session with no profile behind it yet is not "loaded" — without this
    // the moment between the two renders the "no workspace" screen at every
    // sign-in, for as long as the profile query takes.
    setLoading(true);

    let { data: profileRow, error } = await supabase
      /* employees(auto_attendance) rides along with the profile rather than
         being fetched separately: the tracker has to decide whether to run at
         the moment the app opens, and a second round trip is a window in which
         it does not know. Comments stay out of the select string itself —
         supabase-js parses it in the type system and a comment inside makes the
         whole query untyped. */
      .from('user_profiles')
      .select(PROFILE_COLUMNS)
      .eq('id', activeSession.user.id)
      .single();

    /* -----------------------------------------------------------------------
       Retried without the newest columns, once.

       PostgREST refuses a whole select when any one column in it is unknown, so
       a column added by a migration that has not been applied yet does not
       degrade a feature — it fails this query, and this query is the session.
       No company means no currency, no policy and no permissions, which reads
       on screen as every page at once having lost its data.

       That is a deployment-order problem and it will happen again: a build goes
       out on Friday and the migration goes on Monday. So the fallback asks for
       the columns that have always been there, the app runs with the newer
       features quietly absent, and `columnsMissing` says so plainly rather than
       leaving somebody to guess why payroll is blank.
       -------------------------------------------------------------------- */
    if (error?.code === '42703') {
      console.error(
        'The database is missing a column this build reads. Apply the pending '
        + 'migrations — supabase/setup/check-columns.sql lists which.',
        error,
      );
      /* The console was the only place this was said, and a company's browser
         console is read by nobody at the platform. Applying migrations is the
         platform's job, so the platform is told — once per fault, which is what
         reportTechnicalFailure already guarantees — and the banner a company
         sees no longer has to carry the instruction. */
      reportTechnicalFailure(
        technicalDetail(error),
        'fault',
        'load the session (a column this build reads is missing — apply the pending migrations)',
      );
      setColumnsMissing(true);

      const retry = await supabase
        .from('user_profiles')
        .select(PROFILE_COLUMNS_BASE)
        .eq('id', activeSession.user.id)
        .single();

      profileRow = retry.data as typeof profileRow;
      error = retry.error;
    }

    if (!isCurrent()) return;

    /* No profile is not automatically a fault.
       -----------------------------------------------------------------------
       It is also what a platform operator looks like, by design, and the check
       is here rather than first because there is one of them and a thousand of
       everybody else. An operator who has opened a company comes back from this
       with a company, a policy and a synthesised profile holding `*` — which is
       exactly what the database gives them — so every screen in the app works
       without knowing they are not staff. */
    if (error || !profileRow) {
      const asOperator = await loadPlatformContext(activeSession);
      if (!isCurrent()) return;
      if (asOperator) {
        setLoading(false);
        return;
      }
    }

    if (error || !profileRow) {
      /* Two different failures, and only one of them is a fault.
         ---------------------------------------------------------------------
         No row means this account belongs to no company — which is what
         somebody applying to join looks like, and is now the ordinary case
         rather than a broken install. It is left unset so the router can send
         them to the application screen instead of an error page.

         Anything else — a refused grant, a dropped connection — is a real
         fault and says so. Telling them apart matters: the two used to render
         the same screen, and that screen said "run bootstrap.sql", which is
         advice for a developer shown to a customer who has done nothing wrong. */
      const noRow = !error || error.code === 'PGRST116';

      if (!noRow) console.error('Could not load your profile', error);

      /* Asked only here, because this is the only moment it matters and it is
         one round trip that most sessions never make. */
      if (noRow) {
        const { data: state } = await supabase.rpc('account_state');
        if (!isCurrent()) return;
        setAccountState((state as AccountState | null) ?? 'none');
      }

      setProfileError(
        noRow
          ? null
          : error?.code === '42501'
            ? 'The database refused the request: the `app` schema is not readable. '
              + 'Run supabase/migrations/0013_schema_grants.sql.'
            : error?.message ?? null,
      );
      setLoading(false);
      return;
    }

    setProfileError(null);
    setPlatform(null);
    setAccountState('ok');

    const role = profileRow.roles as unknown as {
      code: string; name: string; name_ar?: string | null; permissions: string[];
    } | null;

    /* Optional on the type as well as in the query: the fallback select drops
       `name_ar`, so on a database still short of migration 0102 this is simply
       absent rather than a crash. */
    const employeeRow = profileRow.employees as unknown as {
      auto_attendance?: boolean; full_name_ar?: string | null;
    } | null;
    const companyRow = profileRow.companies as unknown as {
      id: string; name: string; currency: string; timezone: string;
      theme_preset: ThemePreset; locale: string; setup_completed: boolean;
      legal_name: string | null; legal_name_ar: string | null;
      name_ar: string | null;
      address_line: string | null; address_line_ar: string | null;
      bilingual_documents: boolean; date_format: DateFormatId;
      time_format: TimeFormatId;
      trade_licence: string | null;
      /* Letterhead, not screen data — read when a document is built. See
         migration 0175 and the settlement's footer. */
      phone: string | null; email: string | null; website: string | null;
      settlement_contact_name: string | null;
      settlement_contact_phone: string | null;
    } | null;

    setProfile({
      id: profileRow.id,
      companyId: profileRow.company_id,
      employeeId: profileRow.employee_id,
      fullName: profileRow.full_name,
      fullNameAr: employeeRow?.full_name_ar ?? null,
      email: profileRow.email,
      username: profileRow.username,
      avatarPath: profileRow.avatar_path,
      roleCode: role?.code ?? 'employee',
      roleName: role?.name ?? 'Employee',
      roleNameAr: role?.name_ar ?? null,
      permissions: role?.permissions ?? [],
      colorScheme: isColorScheme(profileRow.color_scheme)
        ? profileRow.color_scheme
        : 'system',
      locale: isLanguage(profileRow.locale) ? profileRow.locale : 'en',
      autoAttendance: Boolean(employeeRow?.auto_attendance),
    });

    /* Offer this handset to the push list, once there is a user and a company
       to attach it to.

       Deliberately not awaited. Firebase can take seconds to issue a token and
       occasionally never does, and a man standing at a site gate must not wait
       on a notification registration to reach the scanner. Whatever happens is
       recorded in pushState() and shown on the notification settings screen —
       a failure here is visible, not silent. */
    void registerForPush(profileRow.id, profileRow.company_id);

    if (companyRow) {
      setCompany({
        id: companyRow.id,
        name: companyRow.name,
        currency: companyRow.currency,
        timezone: companyRow.timezone,
        themePreset: isThemePreset(companyRow.theme_preset) ? companyRow.theme_preset : DEFAULT_PRESET,
        locale: companyRow.locale,
        setupCompleted: companyRow.setup_completed,
        legalName: companyRow.legal_name ?? null,
        legalNameAr: companyRow.legal_name_ar ?? null,
        nameAr: companyRow.name_ar ?? null,
        addressLine: companyRow.address_line ?? null,
        addressLineAr: companyRow.address_line_ar ?? null,
        bilingualDocuments: Boolean(companyRow.bilingual_documents),
        dateFormat: isDateFormat(companyRow.date_format) ? companyRow.date_format : DEFAULT_DATE_FORMAT,
        timeFormat: isTimeFormat(companyRow.time_format) ? companyRow.time_format : DEFAULT_TIME_FORMAT,
        tradeLicence: companyRow.trade_licence ?? null,
        phone: companyRow.phone ?? null,
        email: companyRow.email ?? null,
        website: companyRow.website ?? null,
        settlementContactName: companyRow.settlement_contact_name ?? null,
        settlementContactPhone: companyRow.settlement_contact_phone ?? null,
      });
      // The company preset is the admin's decision and applies to everyone.
      if (isThemePreset(companyRow.theme_preset)) setPreset(companyRow.theme_preset);

      // Every "today" in the app is resolved against this. Set before the first
      // screen renders, so no date is ever derived in UTC by default.
      setActiveTimeZone(companyRow.timezone);
      // Which day it is, then how a day is written. Two settings, one place.
      setActiveDateFormat(isDateFormat(companyRow.date_format) ? companyRow.date_format : null);
      setActiveTimeFormat(isTimeFormat(companyRow.time_format) ? companyRow.time_format : null);
    }

    // Light or dark is the user's own, and follows them between devices.
    setScheme(isColorScheme(profileRow.color_scheme) ? profileRow.color_scheme : 'system');

    /* So does the language.
       -----------------------------------------------------------------------
       Per user rather than per company: a contracting office has an accountant
       working in English and a supervisor reading Arabic, signed in to the same
       company at the same moment. Either one being forced into the other's
       language would be permanently wrong for one of them.

       The device has already opened in whatever it remembered, which is right
       far more often than not — the same person on the same handset. This
       corrects it for the case that matters: a new phone, or a shared site
       phone that the last user left in the other language. */
    if (isLanguage(profileRow.locale)) setLanguage(profileRow.locale);

    // Annotate the sign-in the database already recorded.
    //
    // The event itself comes from a trigger on auth.users, so it exists whether
    // or not this runs. All this adds is which handset — useful for "he says
    // the app never opened", useless to a client that lies about it. Fire and
    // forget: a failure here must never block the workspace loading.
    void recordLoginContext();

    await loadPolicy();
    if (!isCurrent()) return;
    setLoading(false);
  }

  /**
   * The signed-in account as a platform operator, if that is what it is.
   *
   * Returns false when it is not one, so the caller can fall through to the
   * ordinary "this account is not linked to a company" message. Returns true
   * having set the whole workspace: for an operator inside a company that means
   * a company, a policy and a profile; for one in the console it means none of
   * those, which is the correct empty state rather than an error.
   */
  async function loadPlatformContext(activeSession: Session): Promise<boolean> {
    const { data: operator } = await supabase
      .from('platform_operators')
      .select(`
        id, full_name, full_name_ar, email, permissions, is_active,
        locale, color_scheme, acting_company_id, acting_reason, acting_since
      `)
      .eq('id', activeSession.user.id)
      .maybeSingle();

    if (!operator || operator.is_active === false) return false;

    const me: PlatformOperator = {
      id: operator.id,
      fullName: operator.full_name,
      fullNameAr: operator.full_name_ar ?? null,
      email: operator.email,
      permissions: operator.permissions ?? [],
      actingCompanyId: operator.acting_company_id ?? null,
      actingReason: operator.acting_reason ?? null,
      actingSince: operator.acting_since ?? null,
    };

    /* Being inside a company wears off after twelve hours — see migration
       0110. The database stops answering at that moment whatever the client
       believes, so this is not the enforcement; it is the difference between
       landing in the console and landing on a workspace where every query
       silently returns nothing.

       Cleared through leave_company() rather than locally, because the visit
       row is still open and the company's own Activity screen would otherwise
       go on saying somebody is inside. */
    const expired = me.actingCompanyId !== null
      && me.actingSince !== null
      && Date.now() - new Date(me.actingSince).getTime() >= ACTING_WINDOW_MS;

    if (expired) {
      await supabase.rpc('leave_company');
      me.actingCompanyId = null;
      me.actingReason = null;
      me.actingSince = null;
    }

    setPlatform(me);
    setProfileError(null);
    setAccountState('platform');

    if (!me.actingCompanyId) {
      // The console. No company, and therefore nothing operational to load.
      setProfile(null);
      setCompany(null);
      setPolicy(null);
      setPolicyGaps([]);
      setScheme(isColorScheme(operator.color_scheme) ? operator.color_scheme : 'system');
      if (isLanguage(operator.locale)) setLanguage(operator.locale);
      return true;
    }

    const { data: companyRow } = await supabase
      .from('companies')
      .select(`
        id, name, currency, timezone, theme_preset, locale, setup_completed,
        legal_name, legal_name_ar, name_ar, address_line, address_line_ar,
        bilingual_documents, date_format, time_format, trade_licence,
        phone, email, website, settlement_contact_name, settlement_contact_phone
      `)
      .eq('id', me.actingCompanyId)
      .maybeSingle();

    /* A profile this person does not have.
       ---------------------------------------------------------------------
       Assembled rather than read, and it is not a trick: `*` is precisely what
       app.current_permissions() answers for an operator inside a company, so
       this agrees with the database rather than getting ahead of it. Anything
       the interface hides that RLS would have allowed is a button somebody
       cannot find; anything it shows that RLS refuses is an error message. The
       two have to say the same thing, and this is where that is arranged. */
    setProfile({
      id: me.id,
      companyId: me.actingCompanyId,
      employeeId: null,
      fullName: me.fullName,
      fullNameAr: me.fullNameAr,
      /* An operator has no user_profiles row, so there is nothing to carry a
         picture on. Initials, which is what everybody without one gets. */
      username: null,
      avatarPath: null,
      email: me.email,
      roleCode: 'platform_operator',
      roleName: 'NoorOne',
      roleNameAr: null,
      permissions: ['*'],
      colorScheme: isColorScheme(operator.color_scheme) ? operator.color_scheme : 'system',
      locale: isLanguage(operator.locale) ? operator.locale : 'en',
      /* No employee behind this account, so nothing to mark automatically.
         False is the right answer here, not a missing one. */
      autoAttendance: false,
    });

    setScheme(isColorScheme(operator.color_scheme) ? operator.color_scheme : 'system');
    if (isLanguage(operator.locale)) setLanguage(operator.locale);

    if (companyRow) {
      setCompany({
        id: companyRow.id,
        name: companyRow.name,
        currency: companyRow.currency,
        timezone: companyRow.timezone,
        themePreset: isThemePreset(companyRow.theme_preset) ? companyRow.theme_preset : DEFAULT_PRESET,
        locale: companyRow.locale,
        setupCompleted: companyRow.setup_completed,
        legalName: companyRow.legal_name ?? null,
        legalNameAr: companyRow.legal_name_ar ?? null,
        nameAr: companyRow.name_ar ?? null,
        addressLine: companyRow.address_line ?? null,
        addressLineAr: companyRow.address_line_ar ?? null,
        bilingualDocuments: Boolean(companyRow.bilingual_documents),
        dateFormat: isDateFormat(companyRow.date_format) ? companyRow.date_format : DEFAULT_DATE_FORMAT,
        timeFormat: isTimeFormat(companyRow.time_format) ? companyRow.time_format : DEFAULT_TIME_FORMAT,
        tradeLicence: companyRow.trade_licence ?? null,
        phone: companyRow.phone ?? null,
        email: companyRow.email ?? null,
        website: companyRow.website ?? null,
        settlementContactName: companyRow.settlement_contact_name ?? null,
        settlementContactPhone: companyRow.settlement_contact_phone ?? null,
      });
      /* The customer's own theme, timezone and date shape. An operator looking
         at a company should see what that company sees — a figure read in the
         wrong timezone or a date read in the wrong order is how somebody
         helpfully corrects something that was never wrong. */
      if (isThemePreset(companyRow.theme_preset)) setPreset(companyRow.theme_preset);
      setActiveTimeZone(companyRow.timezone);
      setActiveDateFormat(isDateFormat(companyRow.date_format) ? companyRow.date_format : null);
      setActiveTimeFormat(isTimeFormat(companyRow.time_format) ? companyRow.time_format : null);
    }

    await loadPolicy();
    return true;
  }

  async function loadPolicy() {
    const today = todayIn();
    const { data } = await supabase
      .from('policy_versions')
      .select('policy')
      .eq('status', 'active')
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`)
      .order('effective_from', { ascending: false })
      .limit(1)
      .maybeSingle();

    const gaps = findPolicyGaps(data?.policy ?? {});
    setPolicyGaps(gaps);
    setPolicy(gaps.length === 0 ? (data?.policy as Policy) : null);
  }

  useEffect(() => {
    // Which account the loaded context belongs to. `onAuthStateChange` also
    // fires on every token refresh and on every return from the background —
    // reloading the whole workspace each time would throw the user back to the
    // boot screen while they were reading a page.
    //
    // Owns its own "nothing loaded yet" state — see authState.ts. The first
    // event always reloads, including a signed-out one, because that call is
    // the only thing that clears `loading`.
    const shouldReload = createAuthGate();

    function sync(next: Session | null, signedIn = false) {
      setSession(next);

      // Recorded here rather than relying on the auth.users trigger alone.
      //
      // That trigger cannot be created on every Supabase project, and it never
      // sees a biometric unlock — which is a token refresh, not a sign-in. The
      // RPC de-duplicates against anything the trigger already wrote, so both
      // mechanisms running is harmless and either one alone is sufficient.
      //
      // Keyed on the SIGNED_IN event specifically: INITIAL_SESSION fires on
      // every page load from a restored session, and counting those would turn
      // the log into a page-view counter.
      if (signedIn && next) {
        void supabase.rpc('record_signin', { p_method: 'password' });
      }

      if (!shouldReload(next?.user.id ?? null)) return;
      void loadContext(next);
    }

    void supabase.auth.getSession().then(({ data }) => sync(data.session));

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      // Deferred deliberately, and the app hangs on sign-in without it.
      //
      // supabase-js runs this callback while *holding* its auth lock. Every
      // `supabase.from(...)` call needs that same lock to attach the access
      // token, so awaiting one from inside here waits on a lock that cannot be
      // released until we return: a deadlock. The profile query never settles,
      // `loading` never clears, and the sign-in spinner runs forever — while a
      // perfectly valid session sits behind it, which is why force-quitting
      // and reopening the app landed straight on the dashboard.
      //
      // A zero-delay timeout is enough: it puts the work in a fresh task, by
      // which point the callback has returned and the lock is free. This is
      // the mitigation Supabase itself documents.
      setTimeout(() => sync(next, event === 'SIGNED_IN'), 0);
    });

    return () => sub.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      profile,
      company,
      policy,
      policyGaps,
      loading,
      profileError,
      accountState,
      platform,
      columnsMissing,
      canPlatform: (permission: string) =>
        !!platform &&
        (platform.permissions.includes('*') || platform.permissions.includes(permission)),
      enterCompany: async (companyId: string, reason: string) => {
        const { error } = await supabase.rpc('enter_company', {
          p_company: companyId,
          p_reason: reason,
        });
        if (error) throw new Error(error.message);
        await loadContext(session);
      },
      leaveCompany: async () => {
        const { error } = await supabase.rpc('leave_company');
        if (error) throw new Error(error.message);
        await loadContext(session);
      },
      can: (permission: string) =>
        !!profile &&
        (profile.permissions.includes('*') || profile.permissions.includes(permission)),
      setColorScheme: (next: ColorScheme) => {
        setScheme(next);
        setProfile((current) => (current ? { ...current, colorScheme: next } : current));

        // Fire and forget. The screen has already changed and localStorage has
        // already recorded it, so a failed write costs the user nothing until
        // they sign in on another device.
        /* An operator has no user_profile to write to — their preferences live
           on their own row. Same write, different table, and the screen has
           already changed either way. */
        const table = platform ? 'platform_operators' : 'user_profiles';
        const who = platform?.id ?? profile?.id;

        if (who) {
          void supabase
            .from(table)
            .update({ color_scheme: next })
            .eq('id', who)
            .then(({ error }) => {
              if (error) console.error('Could not save your appearance setting', error);
            });
        }
      },
      setLocale: (next: Language) => {
        /* The same shape as the appearance setting above, and for the same
           reasons: the screen and the device have already changed, so the write
           is fire-and-forget and a failure costs nothing until this person
           signs in somewhere else. */
        setLanguage(next);
        setProfile((current) => (current ? { ...current, locale: next } : current));

        const table = platform ? 'platform_operators' : 'user_profiles';
        const who = platform?.id ?? profile?.id;

        if (who) {
          void supabase
            .from(table)
            .update({ locale: next })
            .eq('id', who)
            .then(({ error }) => {
              if (error) console.error('Could not save your language', error);
            });
        }
      },
      signOut: async () => {
        /* An operator stops being inside a company before they stop being
           signed in. Without this the visit stays open until they next enter
           somewhere, and the customer's Activity screen says the vendor is in
           their data — for as long as nobody happens to open another company. */
        if (platform?.actingCompanyId) {
          /* Signing out must not be blocked by tidying up. The access expires
             on its own, and the next enter closes the visit — so a failure
             here costs a stale row, not a stuck sign-out. */
          const { error } = await supabase.rpc('leave_company');
          if (error) console.error('Could not close the visit', error);
        }

        /* The handset comes off the push list first.

           A shared site phone signed out by one supervisor and into by the next
           would otherwise keep delivering the first man's notifications to the
           second — his leave decisions, his absence figures. */
        await unregisterPush();

        // A full, global sign-out — every refresh token for the account is
        // revoked, on every device.
        //
        // This used to be scoped to `local` when a fingerprint was enrolled,
        // because the keystore held a refresh token that a global sign-out
        // would kill. It no longer holds one: what it holds is a device secret
        // that does not rotate and is not a session, so signing out properly
        // costs the fingerprint nothing.
        //
        // Turning biometric off entirely stays a separate, deliberate act —
        // the button in Settings, which revokes the enrolment server-side.
        await supabase.auth.signOut();

        /* Both caches, on the way out.
           -------------------------------------------------------------------
           The query cache holds a roster and a site list; the signed-link cache
           holds live URLs to private documents, which outlive the session that
           minted them. On a site office handset the next person to sign in is
           frequently a different person, and neither of those should still be
           sitting there when they do. */
        clearQueryCache();
        forgetAllFiles();
      },
      refresh: async () => {
        const { data } = await supabase.auth.getSession();
        await loadContext(data.session);
      },
    }),
    [session, profile, company, policy, policyGaps, loading, profileError,
      accountState, platform, columnsMissing,
      setScheme, setLanguage],
  );

  /* The boot screen comes down here, and not when React mounts.
     -------------------------------------------------------------------------
     `loading` false is the first moment there is a real screen behind the
     overlay — the workspace, the sign-in page, or a failure. Taking it down at
     mount instead meant handing over to the app's own loading screen, which
     started the same animation again from nothing: a still logo, then the logo
     assembling, then the page.

     This reports readiness rather than taking the overlay down itself: a gate
     further in may still be deciding what to show, and it holds the overlay up
     until it has. See src/core/boot.ts. */
  useEffect(() => {
    if (!loading) bootScreenReady();
  }, [loading]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** Convenience for the many places that only need the permission check. */
export function useCan(): (permission: string) => boolean {
  return useSession().can;
}
